// Format a stored (market-local, Z-suffixed) timestamp using its UTC components,
// so no timezone shift is applied. Blank/invalid -> ''. Shared by the agent exports.

const pad = (n: number): string => String(n).padStart(2, '0')

const parseUtc = (iso?: string | null): Date | null => {
  if (!iso) {
    return null
  }
  const d = new Date(iso)
  return isNaN(d.getTime()) ? null : d
}

/** yyyy-MM-dd from the timestamp's UTC components. */
export const formatUtcDate = (iso?: string | null): string => {
  const d = parseUtc(iso)
  return d ? `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` : ''
}

/** yyyy-MM-dd from a Date's local components (the calendar day a picker shows). */
export const toLocalYmd = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/**
 * "May 6, 2026" from a bare yyyy-MM-dd date string.
 *
 * The input contract differs from the two functions either side of it: those
 * take a Z-suffixed timestamp, this takes a date-only string (release notes
 * carry `releaseDate`, not an instant). Going through Date.UTC on the split
 * parts rather than `new Date(str)` keeps it off the local timezone, which
 * would otherwise render the previous day for anyone west of UTC.
 */
export const formatUtcLongDate = (ymd?: string | null): string => {
  if (!ymd) {
    return ''
  }
  const [y, m, d] = ymd.split('-').map(Number)
  if (y === undefined || m === undefined || d === undefined || isNaN(y) || isNaN(m) || isNaN(d)) {
    return ''
  }
  const dt = new Date(Date.UTC(y, m - 1, d))
  return isNaN(dt.getTime())
    ? ''
    : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
}

/** mm/dd/yyyy hh:mm:ss from the timestamp's UTC components. */
export const formatUtcDateTime = (iso?: string | null): string => {
  const d = parseUtc(iso)
  return d
    ? `${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())}/${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
    : ''
}
